import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { AlertCircle, ArrowLeft, Building2 } from 'lucide-react'
import { apiJson } from '../api'
import { hasAnyRole } from '../auth'

interface Organization {
  id: string
  name: string
  displayName?: string
  registrationNo?: string
  logoUrl?: string
  hqAddress?: string
  phone?: string
  email?: string
  website?: string
  timezone: string
  currency: string
  workDays: string[]
  workStartTime: string
  workEndTime: string
  lateGraceMinutes: number
  standardWorkMinutes: number
}

const DAYS = [
  { key: 'mon', label: 'Mon' },
  { key: 'tue', label: 'Tue' },
  { key: 'wed', label: 'Wed' },
  { key: 'thu', label: 'Thu' },
  { key: 'fri', label: 'Fri' },
  { key: 'sat', label: 'Sat' },
  { key: 'sun', label: 'Sun' },
]

const emptyForm = {
  name: '',
  displayName: '',
  registrationNo: '',
  logoUrl: '',
  hqAddress: '',
  phone: '',
  email: '',
  website: '',
  timezone: 'Asia/Yangon',
  currency: 'MMK',
  workDays: ['mon', 'tue', 'wed', 'thu', 'fri'] as string[],
  workStartTime: '09:00',
  workEndTime: '17:00',
  lateGraceMinutes: '10',
  standardWorkMinutes: '480',
}

const inputCls =
  'w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none transition-all placeholder:text-slate-400 focus:border-brand-400 focus:ring-4 focus:ring-brand-100'
const labelCls = 'mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-400'

export default function CompanyEdit() {
  const navigate = useNavigate()
  const canEdit = hasAnyRole('admin')
  const [orgExists, setOrgExists] = useState(false)
  const [form, setForm] = useState(emptyForm)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!canEdit) return
    apiJson<Organization | null>('/organization')
      .then((org) => {
        setOrgExists(Boolean(org))
        if (org) {
          setForm({
            name: org.name ?? '',
            displayName: org.displayName ?? '',
            registrationNo: org.registrationNo ?? '',
            logoUrl: org.logoUrl ?? '',
            hqAddress: org.hqAddress ?? '',
            phone: org.phone ?? '',
            email: org.email ?? '',
            website: org.website ?? '',
            timezone: org.timezone ?? 'Asia/Yangon',
            currency: org.currency ?? 'MMK',
            workDays: org.workDays ?? ['mon', 'tue', 'wed', 'thu', 'fri'],
            workStartTime: org.workStartTime ?? '09:00',
            workEndTime: org.workEndTime ?? '17:00',
            lateGraceMinutes: String(org.lateGraceMinutes ?? 10),
            standardWorkMinutes: String(org.standardWorkMinutes ?? 480),
          })
        }
        setLoading(false)
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : 'Failed to load company profile')
        setLoading(false)
      })
  }, [canEdit])

  const toggleDay = (key: string) => {
    setForm((f) => ({
      ...f,
      workDays: f.workDays.includes(key) ? f.workDays.filter((d) => d !== key) : [...f.workDays, key],
    }))
  }

  const save = async () => {
    setSaving(true)
    setError('')
    try {
      const payload: Record<string, unknown> = { ...form }
      for (const k of Object.keys(payload)) if (payload[k] === '') delete payload[k]
      if (payload.lateGraceMinutes !== undefined) payload.lateGraceMinutes = Number(form.lateGraceMinutes)
      if (payload.standardWorkMinutes !== undefined) payload.standardWorkMinutes = Number(form.standardWorkMinutes)
      await apiJson<Organization>('/organization', { method: 'PUT', body: JSON.stringify(payload) })
      navigate('/company')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed')
      setSaving(false)
    }
  }

  if (!canEdit) {
    return (
      <div className="flex items-center gap-2.5 rounded-xl border border-amber-100 bg-amber-50 px-4 py-3 text-sm text-amber-700">
        <AlertCircle size={16} className="shrink-0" />
        Only administrators can edit the company profile.
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-brand-600" />
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-3xl space-y-5">
      <Link to="/company" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 transition-colors hover:text-brand-600">
        <ArrowLeft size={15} /> Back to Company
      </Link>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="flex items-center gap-3.5 border-b border-slate-100 px-6 py-5">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-brand-500 to-brand-700 text-white shadow-md shadow-brand-500/25">
            <Building2 size={20} />
          </div>
          <div>
            <h1 className="text-lg font-bold tracking-tight">{orgExists ? 'Edit Company Profile' : 'Provision Organization'}</h1>
            <p className="text-xs text-slate-400">Saved via PUT /organization — idempotent singleton upsert</p>
          </div>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault()
            save()
          }}
          className="px-6 py-6"
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className={labelCls}>Legal name *</label>
              <input className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Zentry Digital Co., Ltd." />
            </div>
            <div>
              <label className={labelCls}>Display name</label>
              <input className={inputCls} value={form.displayName} onChange={(e) => setForm({ ...form, displayName: e.target.value })} placeholder="Zentry Digital" />
            </div>
            <div>
              <label className={labelCls}>Registration no.</label>
              <input className={inputCls} value={form.registrationNo} onChange={(e) => setForm({ ...form, registrationNo: e.target.value })} />
            </div>
            <div>
              <label className={labelCls}>Logo URL</label>
              <input className={inputCls} value={form.logoUrl} onChange={(e) => setForm({ ...form, logoUrl: e.target.value })} placeholder="https://…" />
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls}>Headquarters</label>
              <input className={inputCls} value={form.hqAddress} onChange={(e) => setForm({ ...form, hqAddress: e.target.value })} placeholder="Yangon, Myanmar" />
            </div>
            <div>
              <label className={labelCls}>Phone</label>
              <input className={inputCls} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>
            <div>
              <label className={labelCls}>Email</label>
              <input className={inputCls} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="info@…" />
            </div>
            <div>
              <label className={labelCls}>Website</label>
              <input className={inputCls} value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="https://…" />
            </div>
            <div>
              <label className={labelCls}>Timezone (IANA)</label>
              <input className={inputCls} value={form.timezone} onChange={(e) => setForm({ ...form, timezone: e.target.value })} placeholder="Asia/Yangon" />
            </div>
            <div>
              <label className={labelCls}>Currency (ISO 4217)</label>
              <input
                className={inputCls}
                value={form.currency}
                maxLength={3}
                onChange={(e) => setForm({ ...form, currency: e.target.value.toUpperCase() })}
                placeholder="MMK"
              />
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls}>Work days</label>
              <div className="flex flex-wrap gap-2">
                {DAYS.map((d) => (
                  <button
                    key={d.key}
                    type="button"
                    onClick={() => toggleDay(d.key)}
                    className={`rounded-full px-3.5 py-1.5 text-xs font-semibold ring-1 transition-all ${
                      form.workDays.includes(d.key)
                        ? 'bg-brand-600 text-white ring-brand-600'
                        : 'bg-white text-slate-500 ring-slate-200 hover:bg-slate-50'
                    }`}
                  >
                    {d.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className={labelCls}>Work start (HH:mm)</label>
              <input type="time" className={inputCls} value={form.workStartTime} onChange={(e) => setForm({ ...form, workStartTime: e.target.value })} />
            </div>
            <div>
              <label className={labelCls}>Work end (HH:mm)</label>
              <input type="time" className={inputCls} value={form.workEndTime} onChange={(e) => setForm({ ...form, workEndTime: e.target.value })} />
            </div>
            <div>
              <label className={labelCls}>Late grace (minutes)</label>
              <input
                type="number"
                min={0}
                max={180}
                className={inputCls}
                value={form.lateGraceMinutes}
                onChange={(e) => setForm({ ...form, lateGraceMinutes: e.target.value })}
              />
            </div>
            <div>
              <label className={labelCls}>Standard work minutes / day</label>
              <input
                type="number"
                min={60}
                max={1440}
                step={30}
                className={inputCls}
                value={form.standardWorkMinutes}
                onChange={(e) => setForm({ ...form, standardWorkMinutes: e.target.value })}
              />
              <p className="mt-1 text-[11px] text-slate-400">Worked time beyond this counts as overtime.</p>
            </div>
          </div>

          {error && (
            <p className="mt-4 flex items-center gap-2 rounded-xl bg-red-50 px-4 py-2.5 text-xs font-medium text-red-600">
              <AlertCircle size={14} /> {error}
            </p>
          )}

          <div className="mt-6 flex justify-end gap-3">
            <Link
              to="/company"
              className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-50"
            >
              Cancel
            </Link>
            <button
              type="submit"
              disabled={saving || !form.name || form.workDays.length === 0}
              className="rounded-xl bg-gradient-to-r from-brand-600 to-brand-700 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-brand-500/25 transition-all hover:brightness-110 disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save profile'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
